import React, { useState } from 'react'
import { DeleteOutlined } from '@ant-design/icons'
import { Popconfirm, Tooltip } from 'antd'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { checkPermission } from '../../common/utils/helpers'
import { useApiRequest } from '../../common/hooks'

const ConsumerDeleteButton = ({
  consumer,
  loanData,
  orderData,
  refreshOrder,
}) => {
  const apiRequest = useApiRequest()
  const flags = useFlags()
  const [isDeleting, setIsDeleting] = useState(false)

  const hasDeletePermission = checkPermission('consumer_delete')
  const showDeleteButton = flags?.consumerDeleteButton && hasDeletePermission

  if (!showDeleteButton) {
    return null
  }

  // Check if this consumer can be deleted
  // Cannot delete primary contact if other non-hidden consumers exist
  const visibleConsumers = loanData.consumers.filter(c => !c.hidden)
  const isPrimaryWithOtherConsumers = consumer.is_primary_contact && visibleConsumers.length > 1
  const canDelete = !isPrimaryWithOtherConsumers

  const consumerName = consumer.full_name
    ? consumer.full_name
    : `${consumer.firstname || ''} ${consumer.lastname || ''}`

  const handleDelete = async () => {
    setIsDeleting(true)
    try {
      await apiRequest({
        endpoint: `loans/${loanData.id}/consumers/${consumer.id}`,
        method: 'DELETE',
        successMessage: `${consumer.firstname || ''} ${consumer.lastname || ''} has been removed from this ${orderData ? 'order' : 'loan'}.`,
      })
      refreshOrder()
    } catch (error) {
      // Error is already dispatched by useApiRequest hook
      // The backend returns specific error messages for different scenarios
    } finally {
      setIsDeleting(false)
    }
  }

  if (canDelete) {
    return (
      <Popconfirm
        title={`Are you sure you want to remove ${consumerName} from this ${orderData ? 'order' : 'loan'}?`}
        onConfirm={handleDelete}
        okText="Yes"
        cancelText="No"
        okButtonProps={{ loading: isDeleting }}
      >
        <Tooltip title="Remove contact">
          <DeleteOutlined
            className="contact-delete-icon"
            style={{ color: '#ff4d4f', cursor: 'pointer' }}
            data-testid="deleteConsumerIcon"
          />
        </Tooltip>
      </Popconfirm>
    )
  }

  // Primary contact with other consumers - show disabled button
  return (
    <Tooltip title="Cannot remove primary contact. Assign a new primary contact first.">
      <DeleteOutlined
        className="contact-delete-icon"
        style={{ color: '#d9d9d9', cursor: 'not-allowed' }}
        data-testid="deleteConsumerIconDisabled"
      />
    </Tooltip>
  )
}

export default ConsumerDeleteButton
